import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Play, Loader2, RefreshCw, Sparkles, BookOpen, Shuffle, LayoutGrid } from "lucide-react";
import { toast } from "sonner";
import { API, MASCOT_URL } from "@/lib/chikku";

const CATEGORY_STYLE = {
  "Jataka Tales": { ring: "border-emerald-200", chip: "bg-emerald-100 text-emerald-700", glow: "shadow-emerald-300/40", dot: "bg-emerald-400", tab: "bg-emerald-400 border-emerald-600" },
  Ramayana: { ring: "border-sky-200", chip: "bg-sky-100 text-sky-700", glow: "shadow-sky-300/40", dot: "bg-sky-400", tab: "bg-sky-400 border-sky-600" },
  Mahabharata: { ring: "border-orange-200", chip: "bg-orange-100 text-orange-700", glow: "shadow-orange-300/40", dot: "bg-orange-400", tab: "bg-orange-400 border-orange-600" },
};
const fallbackStyle = { ring: "border-amber-200", chip: "bg-amber-100 text-amber-700", glow: "shadow-amber-300/40", dot: "bg-amber-400", tab: "bg-amber-400 border-amber-600" };
const styleFor = (c) => CATEGORY_STYLE[c] || fallbackStyle;
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const Blobs = () => (
  <div className="pointer-events-none absolute inset-0 overflow-hidden">
    <div className="animate-blob absolute -top-20 -left-24 h-72 w-72 rounded-full bg-sky-200/60 blur-3xl" />
    <div className="animate-blob absolute top-40 -right-24 h-80 w-80 rounded-full bg-amber-200/50 blur-3xl" style={{ animationDelay: "3s" }} />
    <div className="animate-blob absolute bottom-0 left-1/3 h-72 w-72 rounded-full bg-orange-200/50 blur-3xl" style={{ animationDelay: "6s" }} />
  </div>
);

const StoryCard = ({ story, index, onOpen }) => {
  const accent = styleFor(story.category);
  return (
    <motion.button
      data-testid={`story-card-${index}`}
      onClick={() => onOpen(story)}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ delay: (index % 4) * 0.06, type: "spring", stiffness: 240, damping: 20 }}
      whileHover={{ y: -8, rotate: -1 }}
      whileTap={{ scale: 0.96 }}
      className={`group relative text-left bg-white rounded-[2rem] border-4 ${accent.ring} ${accent.glow} shadow-xl p-4 focus:outline-none focus:ring-4 focus:ring-orange-300`}
    >
      <div className="relative overflow-hidden rounded-[1.5rem] aspect-square">
        <img src={story.thumbnailUrl} alt={story.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
        <span className={`absolute top-3 left-3 rounded-full px-3 py-1 text-xs font-extrabold uppercase tracking-wide ${accent.chip}`}>{story.category}</span>
        <div className="absolute bottom-3 right-3 grid h-14 w-14 place-items-center rounded-full bg-orange-400 text-white border-b-4 border-orange-600 shadow-lg transition-transform group-hover:scale-110">
          <Play className="h-7 w-7 fill-white" />
        </div>
      </div>
      <h3 className="font-heading text-xl sm:text-2xl font-bold mt-4 px-1 text-slate-800 leading-tight">{story.title}</h3>
    </motion.button>
  );
};

export default function Home() {
  const navigate = useNavigate();
  const [stories, setStories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeTab, setActiveTab] = useState("all");
  const [status, setStatus] = useState("loading");
  const [surprising, setSurprising] = useState(false);

  const load = async () => {
    setStatus("loading");
    try {
      const [storyRes, catRes] = await Promise.all([
        axios.get(`${API}/stories`),
        axios.get(`${API}/categories`),
      ]);
      setStories(shuffle(storyRes.data));
      setCategories(catRes.data);
      setStatus("ready");
    } catch (e) {
      console.error(e);
      setStatus("error");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const surpriseMe = async () => {
    if (surprising) return;
    setSurprising(true);
    try {
      const cat = activeTab === "all" ? "" : `?category=${encodeURIComponent(activeTab)}`;
      const { data } = await axios.get(`${API}/stories/random${cat}`);
      toast.success(`Chikku picked: ${data.title}!`);
      navigate(`/story/${data.id}`);
    } catch (e) {
      toast.error("Could not find a surprise right now.");
      setSurprising(false);
    }
  };

  const visibleCategories =
    activeTab === "all" ? categories.map((c) => c.name) : [activeTab];

  let cardIndex = -1;

  return (
    <div className="relative min-h-screen">
      <Blobs />
      <div className="relative mx-auto max-w-6xl px-5 sm:px-8 py-8 sm:py-12">
        {/* Header */}
        <header className="flex items-center gap-4 sm:gap-6" data-testid="home-header">
          <motion.img
            src={MASCOT_URL}
            alt="Chikku Krishna mascot"
            data-testid="home-mascot"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 200, damping: 15 }}
            className="h-24 w-24 sm:h-36 sm:w-36 rounded-full border-4 border-white shadow-xl bg-amber-50 object-cover animate-floaty"
          />
          <div>
            <p className="font-heading text-orange-500 font-semibold text-sm sm:text-base flex items-center gap-1">
              <Sparkles className="h-4 w-4" /> Story time with Chikku
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-800 leading-tight">Chikku's Podcast</h1>
            <p className="text-base sm:text-lg text-slate-500 font-semibold mt-1">
              Timeless tales from Jataka, Ramayana &amp; Mahabharata — It's Story Time !!!
            </p>
          </div>
        </header>

        {/* Topic tabs + Surprise */}
        {status === "ready" && stories.length > 0 && (
          <div className="mt-8 sm:mt-10 flex flex-wrap items-center gap-3" data-testid="topic-tabs">
            <button
              onClick={() => setActiveTab("all")}
              data-testid="category-tab-all"
              className={`inline-flex items-center gap-2 rounded-full px-5 py-3 font-heading font-bold text-base border-b-4 transition-transform active:translate-y-1 active:border-b-0 ${
                activeTab === "all" ? "bg-slate-800 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:scale-105"
              }`}
            >
              <LayoutGrid className="h-5 w-5" /> All Stories
            </button>
            {categories.map((c) => {
              const st = styleFor(c.name);
              const active = activeTab === c.name;
              return (
                <button
                  key={c.name}
                  onClick={() => setActiveTab(c.name)}
                  data-testid={`category-tab-${slug(c.name)}`}
                  className={`inline-flex items-center gap-2 rounded-full px-5 py-3 font-heading font-bold text-base border-b-4 transition-transform active:translate-y-1 active:border-b-0 ${
                    active ? `${st.tab} text-white` : "bg-white text-slate-700 border-slate-200 hover:scale-105"
                  }`}
                >
                  {c.name}
                  <span className={`grid h-6 min-w-6 place-items-center rounded-full px-1.5 text-xs ${active ? "bg-white/25" : st.chip}`}>{c.count}</span>
                </button>
              );
            })}

            <button
              onClick={surpriseMe}
              disabled={surprising}
              data-testid="surprise-button"
              className="ml-auto inline-flex items-center gap-2 rounded-full px-6 py-3 font-heading font-bold text-base bg-gradient-to-r from-amber-400 to-orange-400 text-white border-b-4 border-orange-600 shadow-lg transition-transform active:translate-y-1 active:border-b-0 hover:scale-105 disabled:opacity-70"
            >
              {surprising ? <Loader2 className="h-5 w-5 animate-spin" /> : <Shuffle className="h-5 w-5" />} Surprise me!
            </button>
          </div>
        )}

        {/* Content */}
        <section className="mt-8 sm:mt-10">
          {status === "loading" && (
            <div className="grid place-items-center py-24 text-slate-500" data-testid="home-loading">
              <Loader2 className="h-12 w-12 animate-spin text-orange-400" />
              <p className="font-heading text-xl mt-4">Chikku is opening the storybook...</p>
            </div>
          )}

          {status === "error" && (
            <div className="grid place-items-center py-20 text-center" data-testid="home-error">
              <img src={MASCOT_URL} alt="" className="h-24 w-24 rounded-full animate-wiggle mb-4 bg-amber-50" />
              <p className="font-heading text-2xl text-slate-700">Oops! Chikku got a little lost.</p>
              <button
                onClick={load}
                data-testid="home-retry-btn"
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-orange-400 px-6 py-3 font-heading text-lg font-bold text-white border-b-4 border-orange-600 active:translate-y-1 active:border-b-0 transition-transform"
              >
                <RefreshCw className="h-5 w-5" /> Try again
              </button>
            </div>
          )}

          {status === "ready" && stories.length === 0 && (
            <div className="grid place-items-center py-24 text-center" data-testid="home-empty">
              <img src={MASCOT_URL} alt="" className="h-24 w-24 rounded-full animate-floaty mb-4 bg-amber-50" />
              <p className="font-heading text-2xl text-slate-700">No stories yet. Check back soon!</p>
            </div>
          )}

          {status === "ready" && stories.length > 0 && (
            <div className="space-y-12" data-testid="story-grid">
              {visibleCategories.map((cat) => {
                const style = styleFor(cat);
                const list = stories.filter((s) => s.category === cat);
                if (list.length === 0) return null;
                return (
                  <motion.div key={cat} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
                    <div className="flex items-center gap-3 mb-5">
                      <span className={`grid h-10 w-10 place-items-center rounded-2xl ${style.chip}`}>
                        <BookOpen className="h-5 w-5" />
                      </span>
                      <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-800">{cat}</h2>
                      <span className={`h-2 w-2 rounded-full ${style.dot}`} />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                      {list.map((story) => {
                        cardIndex += 1;
                        return <StoryCard key={story.id} story={story} index={cardIndex} onOpen={(s) => navigate(`/story/${s.id}`)} />;
                      })}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
